import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ScanLine, HeartPulse, ShowerHead, QrCode } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import KpiCard from '../components/ui/KpiCard';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DataTable from '../components/ui/DataTable';
import CommandCenterService from '../services/CommandCenterService';
import PatientService from '../services/PatientService';

export default function PatientApp() {
  const [patientModalOpen, setPatientModalOpen] = useState(false);
  const [patients, setPatients] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const commandResponse = await CommandCenterService.getStats();
        const activeIncident = commandResponse?.data?.activeIncident || null;

        if (activeIncident) {
          const { entity } = await PatientService.findByAnnouncement(activeIncident.id);
          setPatients(entity || []);
        } else {
          setPatients([]);
        }
      } catch (error) {
        console.log(error);
      }
    };

    load();
    const intervalId = setInterval(load, 15000);
    return () => clearInterval(intervalId);
  }, []);

  const focusPatient = patients[0];
  const redCount = patients.filter((p) => p.triage === 'Red').length;
  const deconPendingCount = patients.filter((p) => p.decon !== 'Complete').length;
  const handoffReadyCount = patients.filter((p) => p.destination && p.destination !== 'Pending' && p.cbrne?.includes('Clear')).length;

  const kpis = [
    { label: 'Registered', value: patients.length, note: 'Unique ID, no personal data shown', icon: ScanLine, type: 'blue' },
    { label: 'Red Zone', value: redCount, note: 'Immediate clinical visibility', icon: HeartPulse, type: 'red' },
    { label: 'Decon Pending', value: deconPendingCount, note: 'Awaiting decontamination', icon: ShowerHead, type: 'amber' },
    { label: 'QR/NFC Ready', value: handoffReadyCount, note: 'Ambulance transfer package', icon: QrCode, type: 'green' },
  ];

  const headCells = [
    { id: 'id', label: 'Patient ID' },
    { id: 'triage', label: 'Triage', render: (row) => <Badge>{row.triage}</Badge> },
    { id: 'zone', label: 'Zone' },
    { id: 'vitals', label: 'Vitals' },
    { id: 'cbrne', label: 'CBRNE Result' },
    { id: 'decon', label: 'Decon', render: (row) => <Badge>{row.decon}</Badge> },
    { id: 'destination', label: 'Destination' },
    { id: 'sync', label: 'Sync', render: (row) => <Badge>{row.sync}</Badge> },
  ];

  return (
    <Sidebar title="Patient Documentation">
      <section className="space-y-6">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {kpis.map((k) => (
            <KpiCard key={k.label} {...k} />
          ))}
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-100 flex justify-between items-center">
            <h3 className="font-bold text-neutral-800">Patient Documentation Dashboard</h3>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={!focusPatient} onClick={() => setPatientModalOpen(true)}>
                Open Patient File
              </Button>
              <Button onClick={() => toast.success('QR/NFC handoff package created.')}>Generate QR/NFC</Button>
            </div>
          </div>
          {patients.length > 0 ? (
            <DataTable headCells={headCells} rows={patients} />
          ) : (
            <div className="text-sm text-neutral-400 text-center py-10">No patients registered for the active incident</div>
          )}
        </div>
      </section>

      {focusPatient && (
        <Modal
          open={patientModalOpen}
          onClose={() => setPatientModalOpen(false)}
          title="Patient Documentation View"
          subtitle="Patient tablet data: unique ID, vitals, triage, decon and QR/NFC handoff."
          confirmLabel="Open Full File"
          onConfirm={() => {
            setPatientModalOpen(false);
            toast.success(`${focusPatient.id} full file opened.`);
          }}
        >
          <div className="space-y-3">
            <div className="summary-line border border-neutral-200 rounded-xl bg-white p-3 text-sm font-medium text-neutral-600">
              <b className="text-neutral-800">Patient:</b> {focusPatient.id}
            </div>
            <div className="summary-line border border-neutral-200 rounded-xl bg-white p-3 text-sm font-medium text-neutral-600">
              <b className="text-neutral-800">Vitals:</b> {focusPatient.vitals}
            </div>
            <div className="summary-line border border-neutral-200 rounded-xl bg-white p-3 text-sm font-medium text-neutral-600">
              <b className="text-neutral-800">CBRNE:</b> {focusPatient.cbrne}
            </div>
            <div className="summary-line border border-neutral-200 rounded-xl bg-white p-3 text-sm font-medium text-neutral-600">
              <b className="text-neutral-800">Decon:</b> {focusPatient.decon}
            </div>
            <div className="summary-line border border-neutral-200 rounded-xl bg-white p-3 text-sm font-medium text-neutral-600">
              <b className="text-neutral-800">Handoff:</b> QR/NFC package pending
            </div>
          </div>
        </Modal>
      )}
    </Sidebar>
  );
}
